/**
 * Hook for the Create User form: state, validation, and submit.
 */
import React from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { createHandleChange, clearFormAlerts } from '../common';
import { createUser } from '../../services/createUserService';
import { emptyFormData } from './createUserFormUtils';

export function useCreateUserForm() {
  const { token } = useAuth();
  const [formData, setFormData] = React.useState(emptyFormData());
  const [formError, setFormError] = React.useState('');
  const [formSuccess, setFormSuccess] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);

  const handleChange = React.useMemo(
    () => createHandleChange(setFormData, setFormError, setFormSuccess),
    [setFormData, setFormError, setFormSuccess]
  );

  const handleSubmit = React.useCallback(
    async (e) => {
      e.preventDefault();
      clearFormAlerts(setFormError, setFormSuccess);
      setSubmitting(true);
      try {
        if (!formData.email?.trim()) {
          setFormError('Email is required.');
          return;
        }
        if (!formData.password) {
          setFormError('Password is required.');
          return;
        }
        if (formData.password !== formData.confirmPassword) {
          setFormError('Password and confirmation do not match.');
          return;
        }
        if (!formData.role?.trim()) {
          setFormError('Role is required.');
          return;
        }
        const result = await createUser(token, {
          email: formData.email.trim(),
          password: formData.password,
          first_name: formData.first_name?.trim() || null,
          last_name: formData.last_name?.trim() || null,
          role_names: [formData.role]
        });
        if (result.success) {
          setFormSuccess('User created successfully.');
          setFormData(emptyFormData());
        } else {
          setFormError(result.error || 'Failed to create user.');
        }
      } catch (err) {
        setFormError(err.message || 'Failed to create user.');
      } finally {
        setSubmitting(false);
      }
    },
    [token, formData]
  );

  return {
    formData,
    formError,
    formSuccess,
    submitting,
    handleChange,
    handleSubmit
  };
}
